import { useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Typography, Tooltip, CircularProgress } from '@material-ui/core';
import GitHubIcon from '@material-ui/icons/GitHub';
import CloudIcon from '@material-ui/icons/Cloud';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import { useApi, githubAuthApiRef, googleAuthApiRef } from '@backstage/core-plugin-api';
import { useSidebarOpenState } from '@backstage/core-components';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(1, 2),
  },
  authItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1),
    borderRadius: 4,
    cursor: 'pointer',
    color: (theme.palette as any).navigation?.color || '#FFFFFF',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: (theme.palette as any).navigation?.navItem?.hoverBackground || 'rgba(255, 255, 255, 0.1)',
    },
    textDecoration: 'none',
  },
  authItemClosed: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(1),
    borderRadius: 4,
    cursor: 'pointer',
    color: (theme.palette as any).navigation?.color || '#FFFFFF',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: (theme.palette as any).navigation?.navItem?.hoverBackground || 'rgba(255, 255, 255, 0.1)',
    },
    textDecoration: 'none',
  },
  icon: {
    fontSize: 20,
    opacity: 0.9,
  },
  signInIcon: {
    fontSize: 16,
    opacity: 0.7,
  },
  text: {
    fontSize: '0.8rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 140,
  },
  signInText: {
    fontSize: '0.75rem',
    opacity: 0.7,
  },
  loggedInBadge: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: '#4caf50',
    position: 'absolute',
    bottom: -2,
    right: -2,
  },
  iconWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(1),
  },
}));

interface AuthProfile {
  displayName?: string;
  email?: string;
}

interface ProviderStatus {
  connected: boolean;
  profile?: AuthProfile;
}

export const SidebarAuthStatus = () => {
  const classes = useStyles();
  const githubAuth = useApi(githubAuthApiRef);
  const googleAuth = useApi(googleAuthApiRef);
  const { isOpen } = useSidebarOpenState();

  const [githubStatus, setGithubStatus] = useState<ProviderStatus>({ connected: false });
  const [googleStatus, setGoogleStatus] = useState<ProviderStatus>({ connected: false });
  const [loading, setLoading] = useState(true);

  const checkSessions = useCallback(async () => {
    // Check GitHub session using Backstage auth API
    try {
      const githubToken = await githubAuth.getAccessToken(['user'], { optional: true });
      if (githubToken) {
        const profile = await githubAuth.getProfile();
        setGithubStatus({ connected: true, profile });
      } else {
        setGithubStatus({ connected: false });
      }
    } catch {
      setGithubStatus({ connected: false });
    }

    // Check Google session using Backstage auth API
    try {
      const googleToken = await googleAuth.getAccessToken(['openid', 'profile', 'email'], { optional: true });
      if (googleToken) {
        const profile = await googleAuth.getProfile();
        setGoogleStatus({ connected: true, profile });
      } else {
        setGoogleStatus({ connected: false });
      }
    } catch {
      setGoogleStatus({ connected: false });
    }

    setLoading(false);
  }, [githubAuth, googleAuth]);

  useEffect(() => {
    checkSessions();
    const interval = setInterval(checkSessions, 30000);
    const handleFocus = () => checkSessions();
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [checkSessions]);

  const handleGithubSignIn = async () => {
    try {
      await githubAuth.getAccessToken(['user']);
      const profile = await githubAuth.getProfile();
      setGithubStatus({ connected: true, profile });
    } catch (error) {
      console.error('GitHub sign in failed:', error);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      await googleAuth.getAccessToken(['openid', 'profile', 'email']);
      const profile = await googleAuth.getProfile();
      setGoogleStatus({ connected: true, profile });
    } catch (error) {
      console.error('Google sign in failed:', error);
    }
  };

  if (loading) {
    return (
      <Box className={classes.container}>
        <Box className={classes.loading}>
          <CircularProgress size={16} />
        </Box>
      </Box>
    );
  }

  const githubIdentity = githubStatus.profile?.displayName;
  const googleIdentity = googleStatus.profile?.email || googleStatus.profile?.displayName;

  return (
    <Box className={classes.container}>
      {/* GitHub Status */}
      {githubStatus.connected ? (
        <Tooltip title={`GitHub: ${githubIdentity}`} placement="right" arrow>
          <Box className={isOpen ? classes.authItem : classes.authItemClosed}>
            <Box className={classes.iconWrapper}>
              <GitHubIcon className={classes.icon} />
              <Box className={classes.loggedInBadge} />
            </Box>
            {isOpen && (
              <Typography className={classes.text}>
                {githubIdentity}
              </Typography>
            )}
          </Box>
        </Tooltip>
      ) : (
        <Tooltip title="Sign in to GitHub" placement="right" arrow>
          <Box
            className={isOpen ? classes.authItem : classes.authItemClosed}
            onClick={handleGithubSignIn}
          >
            <GitHubIcon className={classes.icon} style={{ opacity: 0.5 }} />
            {isOpen && (
              <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                <Typography className={classes.signInText}>
                  Sign in
                </Typography>
                <PersonAddIcon className={classes.signInIcon} />
              </Box>
            )}
          </Box>
        </Tooltip>
      )}

      {/* Google/GCP Status */}
      {googleStatus.connected ? (
        <Tooltip title={`GCP: ${googleIdentity}`} placement="right" arrow>
          <Box className={isOpen ? classes.authItem : classes.authItemClosed}>
            <Box className={classes.iconWrapper}>
              <CloudIcon className={classes.icon} />
              <Box className={classes.loggedInBadge} />
            </Box>
            {isOpen && (
              <Typography className={classes.text}>
                {googleIdentity}
              </Typography>
            )}
          </Box>
        </Tooltip>
      ) : (
        <Tooltip title="Sign in to Google Cloud" placement="right" arrow>
          <Box
            className={isOpen ? classes.authItem : classes.authItemClosed}
            onClick={handleGoogleSignIn}
          >
            <CloudIcon className={classes.icon} style={{ opacity: 0.5 }} />
            {isOpen && (
              <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                <Typography className={classes.signInText}>
                  Sign in
                </Typography>
                <PersonAddIcon className={classes.signInIcon} />
              </Box>
            )}
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};
